import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Database } from "@angular/fire/database";
import {
  getDownloadURL,
  ref as refStorage,
  Storage,
} from "@angular/fire/storage";
import {
  catchError,
  firstValueFrom,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from "rxjs";
import { environment } from "../../../environments/environment";
import { EntradaService } from "../../entrada/entrada.service";
import { IDescargas } from "../../formas-envio/acuerdo/table-mrb/table-mrb.component";
import {
  AcuerdoRequest,
  IAnio,
  ICNuevoTema,
  IMes,
  IMoverAcuerdo,
} from "../../interfaces/acuerdo";
import {
  IAcuerdo,
  IAcuerdos,
  IArchivoEntrada,
  IEntregablesArchivosEntrada,
  IRAcuerdo,
  IRNAcuerdo,
  IRNAcuerdoNotificacion,
  IRNAcuerdoNotiNuevoTema,
  ITerminarTemaDespacho,
  ITerminarTemaDespachoNotificacion,
  nuevoAcuerdo,
} from "../../interfaces/acuerdos";
import { IListaTemas } from "../../interfaces/doownloadFile";
import { DespachoService } from "../despacho/despacho.service";
import { AppState } from "../../app.state";
import { Store } from "@ngrx/store";
import { setContador, setContadorByAcuerdo, setContadorSuccess } from "../../states/despacho/actions";

@Injectable({
  providedIn: "root",
})
export class AcuerdosService {
  private database: Database = inject(Database);
  private storage = inject(Storage);
  constructor(
    private http: HttpClient,
    private entradaService: EntradaService,
    private despachoService: DespachoService,
    private store: Store<AppState>
  ) {}


  getAcuerdos(): Observable<any> {
    return this.http
      .post<IRAcuerdo>(`${environment.acuerdos.consultarAcuerdos}/1/4`, {})
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data) => {
          return data;
        }),
        catchError((error) => {
          console.error("Error en obtenerAcuerdos", error);
          return of({ error: true, acuerdos: [], items: 0 });
        })
      );
  }

  obtenerAcuerdos(
    tipoAcuerdo: string | number,
    mes: string | number,
    anio: string | number
  ): Observable<IRAcuerdo> {
    return this.http
      .post<IRAcuerdo>(
        `${environment.acuerdos.consultarAcuerdos}/1/${tipoAcuerdo}`,
        {
          mes,
          anio,
        }
      )
      .pipe(
        take(1),
        map((data: IRAcuerdo) => {
          if (data.acuerdos) {
            data.acuerdos.forEach((acuerdo: any) => {
              acuerdo.expanded = false;
            });
            const fechaActual = new Date();
            data.acuerdos.sort((a, b) => {
              const dateA = new Date(a.fechaAcuerdo);
              const dateB = new Date(b.fechaAcuerdo);
              const diffA = Math.abs(dateA.getTime() - fechaActual.getTime());
              const diffB = Math.abs(dateB.getTime() - fechaActual.getTime());
              return diffA - diffB;
            });
          }
          return data;
        }),
        catchError((error) => {
          console.error("Error en obtenerAcuerdos", error);
          return of({ error: true, acuerdos: [], items: 0 });
        })
      );
  }

  detalleAcuerdo(idAcuerdo: string): Observable<any> {
    return this.http
      .get<any>(`${environment.acuerdos.detalleAcuerdoMR}/${idAcuerdo}/false`)
      .pipe(
        take(1),
        map((data: any) => {
          if (data && data.temas && Array.isArray(data.temas)) {
            data.temas.forEach((tema: any) => {
              tema.expanded = false;
              tema.nuevoTema = false;
            });
          }
          return data;
        }),
        catchError((error) => {
          console.error("Error en detalleAcuerdo", error);
          return of({ error: true, temas: [] });
        })
      );
  }

  downloadZIPFilesTemas(paylod: IDescargas | undefined): Observable<any> {
    return this.http
      .post<any>(`${environment.temas.descargar}`, { ...paylod })
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return data;
        }),
        catchError((error) => {
          console.error("Error en downloadZIPFilesTemas", error);
          return of({ error: true });
        })
      );
  }

  downloadListaTemas(paylod: IListaTemas): Observable<any> {
    return this.http
      .post(
        `${environment.acuerdos.getListaTemas}`,
        { ...paylod },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "arraybuffer",
        }
      )
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return { error: false, data };
        }),
        catchError((error) => {
          console.error("Error fetching acuerdos", error);
          return of({ error: true });
        })
      );
  }

  editarAcuerdo(paylod: AcuerdoRequest): Observable<any> {
    return this.http
      .post<any>(`${environment.acuerdos.editarAcuerdo}`, { ...paylod })
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return data;
        }),
        catchError((error) => {
          console.error("Error fetching acuerdos", error);
          return of({ error: true });
        })
      );
  }

  nuevoTema(paylod: ICNuevoTema): Observable<any> {
    return this.http
      .post<any>(`${environment.acuerdos.notificacion}/mrb/nuevoTema`, {
        ...paylod,
      })
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return { error: false, data };
        }),
        catchError((error) => {
          console.error("Error fetching acuerdos", error);
          return of({ error: true });
        })
      );
  }

  getAllAcuerdos(): Observable<IAcuerdos> {
    const acuerdos1$ = this.obtenerAcuerdos(1, "", "");
    const acuerdos2$ = this.obtenerAcuerdos(2, "", "");
    const acuerdos3$ = this.obtenerAcuerdos(3, "", "");

    return forkJoin({
      acuerdos1: acuerdos1$,
      acuerdos2: acuerdos2$,
      acuerdos3: acuerdos3$,
    }).pipe(
      map(({ acuerdos1, acuerdos2, acuerdos3 }) => {
        let data: IAcuerdos = {
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 1,
          nuevoAcuerdo: nuevoAcuerdo,
          // meses: [],
          // anios: [],
        };

        if (!acuerdos2.error) {
          const acuerdos: IAcuerdo[] = acuerdos2.acuerdos || [];

          const acuerdoActual: IAcuerdo[] = acuerdos1.error
            ? [acuerdos2.acuerdos[0]]
            : [acuerdos1.acuerdos[0]];

          data.acuerdos = acuerdos;
          data.acuerdoActual = acuerdoActual;
        }

        if (!acuerdos3.error) {
          const historico: IAcuerdo[] = acuerdos3.acuerdos || [];
          data.historico = historico;
        }

        // data.meses = this.getMeses();
        // data.anios = this.getYears();
        return data;
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 1,
          meses: [],
          anios: [],
          nuevoAcuerdo: nuevoAcuerdo,
        });
      })
    );
  }
  getAllAcuerdos_1_2(): Observable<IAcuerdos> {
    const acuerdos1$ = this.obtenerAcuerdos(1, "", "");
    const acuerdos2$ = this.obtenerAcuerdos(2, "", "");

    return forkJoin({
      acuerdos1: acuerdos1$,
      acuerdos2: acuerdos2$,
    }).pipe(
      map(({ acuerdos1, acuerdos2 }) => {
        let data: IAcuerdos = {
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 1,
          nuevoAcuerdo: nuevoAcuerdo,
        };

        if (!acuerdos2.error) {
          const acuerdos: IAcuerdo[] = acuerdos2.acuerdos || [];

          const acuerdoActual: IAcuerdo[] = acuerdos1.error
            ? [acuerdos2.acuerdos[0]]
            : [acuerdos1.acuerdos[0]];

          data.acuerdos = acuerdos;
          data.acuerdoActual = acuerdoActual;
        }
        return data;
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 1,
          nuevoAcuerdo: nuevoAcuerdo,
        });
      })
    );
  }

  getAllAcuerdosByTipo(tipoAcuerdo: number): Observable<IAcuerdos> {
    const initialData: IAcuerdos = {
      acuerdos: [],
      historico: [],
      acuerdoActual: [],
      tipoAcuerdo: tipoAcuerdo,
      nuevoAcuerdo: nuevoAcuerdo,
    };

    const acuerdos$ = this.obtenerAcuerdos(tipoAcuerdo, "", "");

    const despacho$ = this.despachoService.getInfoDespacho().pipe(take(1));
  let contador = {
    acuerdo: "",
    despacho: "",
  };
    return forkJoin([acuerdos$, despacho$]).pipe(
      switchMap(([acuerdos, despacho]) => {
        if (acuerdos.error) {
          if (tipoAcuerdo === 1) {
            return this.obtenerAcuerdos(2, "", "").pipe(
              map((acuerdos2) => {
                if (!acuerdos2.error) {
                  initialData.acuerdoActual = initialData.acuerdoActual =
                    acuerdos2.acuerdos.length > 0
                      ? [acuerdos2.acuerdos[0]]
                      : [];
                        contador.acuerdo =
                          initialData.acuerdoActual[0].temas.toString();
                        contador.despacho = `${despacho.vobo}-${despacho.informativo}`;
                        this.store.dispatch(
                          setContadorSuccess({
                            contador,
                          })
                        );
                }
               

                return initialData;
              }),
              catchError(() => of(initialData))
            );
          }
          return of(initialData);
        } else {
          if (tipoAcuerdo === 1) {
            initialData.acuerdoActual = [acuerdos.acuerdos[0]];
            contador.acuerdo = initialData.acuerdoActual[0].temas.toString();
            contador.despacho = `${despacho.vobo}-${despacho.informativo}`;
            this.store.dispatch(
              setContadorSuccess({
                contador
              })
            );
          }
          if (tipoAcuerdo === 2) {
            this.store.dispatch(setContador());
            initialData.acuerdos = acuerdos.acuerdos || [];
             this.store.dispatch(
               setContadorByAcuerdo({
                 infoDespacho: despacho,
                 tipoAcuerdo: 1,
               })
             );
          } else if (tipoAcuerdo === 3) {
            this.store.dispatch(setContador());
            initialData.historico = acuerdos.acuerdos || [];
             this.store.dispatch(
               setContadorByAcuerdo({
                 infoDespacho: despacho,
                 tipoAcuerdo: 1,
               })
             );
          }
          return of(initialData);
        }
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of(initialData);
      })
    );
  }

  getAllAcuerdosHistorico(): Observable<IAcuerdos> {
    const acuerdos3$ = this.obtenerAcuerdos(3, "", "");

    return forkJoin({
      acuerdos3: acuerdos3$,
    }).pipe(
      map(({ acuerdos3 }) => {
        let data: IAcuerdos = {
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 3,
          nuevoAcuerdo: nuevoAcuerdo,
        };

        if (!acuerdos3.error) {
          const historico: IAcuerdo[] = acuerdos3.acuerdos || [];
          data.historico = historico;
        }
        return data;
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          acuerdos: [],
          historico: [],
          acuerdoActual: [],
          tipoAcuerdo: 1,
          meses: [],
          anios: [],
          nuevoAcuerdo: nuevoAcuerdo,
        });
      })
    );
  }

  getAcuerdoActual(depacho1: any): Observable<any> {
    const acuerdos1$ = this.obtenerAcuerdos(1, "", "").pipe(take(1));

    return forkJoin([acuerdos1$]).pipe(
      switchMap(([acuerdos1]) => {
        let contador = {
          acuerdo: "",
          despacho: "",
        };

        if (depacho1) {
          console.log("despacho infoo", depacho1);
          contador.despacho = `${depacho1.vobo}-${depacho1.informativo}`;
        }

        if (acuerdos1 && acuerdos1.error) {
          return this.obtenerAcuerdos(2, "", "").pipe(
            map((res) => {
              if (res && !res.error) {
                const acuerdoActual: IAcuerdo[] = res.acuerdos;
                console.log("acuerdoActual", acuerdoActual[0].temas);
                contador.acuerdo = acuerdoActual[0].temas.toString();
              }
              return contador;
            }),
            catchError(() => of(contador))
          );
        }

        if (
          acuerdos1 &&
          !acuerdos1.error
        ) {
          const acuerdoActual: IAcuerdo[] = acuerdos1.acuerdos;
          console.log("acuerdoActual", acuerdoActual[0].temas);
          contador.acuerdo = acuerdoActual[0].temas.toString();
        }
        return of(contador);
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          acuerdo: "",
          despacho: "",
        });
      })
    );
  }

  getYears(): IAnio[] {
    const startYear = 2019;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = startYear, id = 1; i <= currentYear; i++, id++) {
      years.push({ anio: i, id: id });
    }

    return years;
  }
  getMeses(): IMes[] {
    return [
      { mes: "Enero", id: 1 },
      { mes: "Febrero", id: 2 },
      { mes: "Marzo", id: 3 },
      { mes: "Abril", id: 4 },
      { mes: "Mayo", id: 5 },
      { mes: "Junio", id: 6 },
      { mes: "Julio", id: 7 },
      { mes: "Agosto", id: 8 },
      { mes: "Septiembre", id: 9 },
      { mes: "Octubre", id: 10 },
      { mes: "Noviembre", id: 11 },
      { mes: "Diciembre", id: 12 },
    ];
  }

  consultarArchivosPorEntrada(idEntrada: string): Observable<any> {
    return this.http
      .get<any>(`${environment.acuerdos.consultarArchivosPorEntrada}/${idEntrada}`)
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return { error: false, data };
        }),
        catchError((error) => {
          console.error("Error fetching acuerdos", error);
          return of({ error: true });
        })
      );
  }

  consultarEntregables(idEntrada: string): Observable<any> {
    return this.http
      .get<any>(`${environment.acuerdos.consultaEntregables}/${idEntrada}`)
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return { error: false, data };
        }),
        catchError((error) => {
          console.error("Error fetching acuerdos", error);
          return of({ error: true });
        })
      );
  }

  async getEntregableArchivos(
    id_entrada: string
  ): Promise<Observable<IEntregablesArchivosEntrada>> {
    const res1$ = this.consultarArchivosPorEntrada(id_entrada);
    const res2$ = this.consultarEntregables(id_entrada);

    return forkJoin({
      res1: res1$,
      res2: res2$,
    }).pipe(
      mergeMap(({ res1, res2 }) => {
        const data: IEntregablesArchivosEntrada = {
          archivos: [],
          entregables: [],
        };

        // Create observables for each asynchronous operation
        const archivos$ = !res1.data.error
          ? from(
              this.getArchivosEntrada(id_entrada, res1.data.archivos_adjuntos)
            )
          : of([]);

        const entregables$ = !res2.data.error
          ? of(res2.data.entregables)
          : of([]);

        return forkJoin([archivos$, entregables$]).pipe(
          map(([archivosAdjuntos, entregables]) => {
            data.archivos = archivosAdjuntos;
            data.entregables = entregables;
            return data;
          })
        );
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          archivos: [],
          entregables: [],
        });
      })
    );
  }

  // async getArchivosEntrada(
  //   idEntrada: string,
  //   archivos: IArchivoEntrada[]
  // ): Promise<IArchivoEntrada[]> {
  //   const archivosURL = await Promise.all(
  //     archivos.map(async (item: IArchivoEntrada) => {
  //       const filePath = `entradas/${idEntrada}/${item.nombre_archivo}`;
  //       const storageRef = refStorage(this.storage, filePath);
  //       const url = await getDownloadURL(storageRef);
  //       item.url = url;
  //       return item;
  //     })
  //   );

  //   return archivosURL;
  // }
  async getArchivosEntrada(
    idEntrada: string,
    archivos: IArchivoEntrada[]
  ): Promise<IArchivoEntrada[]> {
    const archivosWithUrls = await Promise.all(
      archivos.map(async (item: IArchivoEntrada) => {
        try {
          const filePath = `entradas/${idEntrada}/${item.nombre_archivo}`;
          const storageRef = refStorage(this.storage, filePath);
          const url = await getDownloadURL(storageRef);
          return { ...item, url }; // Return item with URL
        } catch (error) {
          return { ...item, url: "" }; // Return item with empty URL on error
        }
      })
    );

    return archivosWithUrls;
  }

  exportAcuerdos(acuerdos: any[]): Observable<any> {
    return this.http
      .post(
        `${environment.acuerdos.exportAcuerdos}`,
        { acuerdos },
        {
          headers: { "Content-Type": "application/json" },
          responseType: "arraybuffer",
        }
      )
      .pipe(
        map((data: any) => {
          console.log("data post", data);
          return data;
        }),
        catchError((error: any) => {
          return of({
            error: true,
            message: "No se encontrarón datos para esta consulta.",
          } as unknown as any);
        })
      );
  }

  getContadores(): Observable<any> {
    const acuerdos1$ = this.obtenerAcuerdos(1, "", "").pipe(take(1));
    const depacho1$ = this.despachoService.getInfoDespacho().pipe(take(1));

    return forkJoin([acuerdos1$, depacho1$]).pipe(
      switchMap(([acuerdos1, depacho1]) => {
        let contador = {
          acuerdo: "",
          despacho: "",
        };

        if (depacho1 && !depacho1.error) {
          console.log("despacho infoo", depacho1);
          contador.despacho = `${depacho1.vobo}-${depacho1.informativo}`;
        }

        if (acuerdos1 && acuerdos1.error) {
          return this.obtenerAcuerdos(2, "", "").pipe(
            map((res) => {
              if (
                res &&
                !res.error &&
                res.acuerdos &&
                res.acuerdos.length > 0
              ) {
                const acuerdoActual: IAcuerdo[] = res.acuerdos;
                console.log("acuerdoActual", acuerdoActual[0].temas);
                contador.acuerdo = acuerdoActual[0].temas.toString();
              }
              return contador;
            }),
            catchError(() => of(contador))
          );
        }

        if (
          acuerdos1 &&
          !acuerdos1.error &&
          acuerdos1.acuerdos &&
          acuerdos1.acuerdos.length > 0
        ) {
          const acuerdoActual: IAcuerdo[] = acuerdos1.acuerdos;
          console.log("acuerdoActual", acuerdoActual[0].temas);
          contador.acuerdo = acuerdoActual[0].temas.toString();
        }
        return of(contador);
      }),
      catchError((error) => {
        console.error("Error fetching data:", error);
        return of({
          acuerdo: "",
          despacho: "",
        });
      })
    );
  }

  consultaFechasAcuerdo(): Observable<any> {
    return this.http.get(`${environment.acuerdos.consultaFechasAcuerdo}`).pipe(
      map((data: any) => {
        console.log("data post", data);
        return data;
      }),
      catchError((error: any) => {
        return of({
          error: true,
          message: "No se encontrarón datos para esta consulta.",
        } as unknown as any);
      })
    );
  }

  terminarTema(payload: ITerminarTemaDespacho): Observable<any> {
    return this.http.post(`${environment.acuerdos.terminarTema}`, payload).pipe(
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        return of({
          error: true,
          message: "Error en terminarTema",
        } as unknown as any);
      })
    );
  }

  terminarTemaAcuerdo(payload: any): Promise<any> {
    return firstValueFrom(
      this.http.post(`${environment.acuerdos.terminarTema}`, payload)
    );
  }

  terminarTemaDespacho(payload: ITerminarTemaDespacho): Promise<any> {
    return firstValueFrom(
      this.http.post(`${environment.despacho.terminarTema}`, payload)
    );
  }

  notificacionTerminarTema(
    payload: ITerminarTemaDespachoNotificacion
  ): Promise<any> {
    return firstValueFrom(
      this.http.post(
        `${environment.acuerdos.notificacion}/despacho/temaTerminado`,
        payload
      )
    );
  }

  notificacionTerminarTemaAcuerdo(payload: any): Promise<any> {
    return firstValueFrom(
      this.http.post(
        `${environment.acuerdos.notificacion}/mrb/temaTerminado`,
        payload
      )
    );
  }

  consultarJustificacionTerminarTema(idTema: string): Promise<any> {
    return firstValueFrom(
      this.http.get(
        `${environment.acuerdos.consultarTerminar}/${idTema}`
      )
    );
  }

  moverTemaAcuerdoMrBDespacho(payload: IMoverAcuerdo): Promise<any> {
    return firstValueFrom(
      this.http.post(
        `${environment.acuerdos.moverTemaAcuerdoMrBDespacho}`,
        payload
      )
    );
  }
  agregarAcuerdoMrb(payload: IRNAcuerdo): Promise<any> {
    return firstValueFrom(
      this.http.post(`${environment.acuerdos.agregarAcuerdo}`, payload)
    );
  }

  nuevoAcuerdoMrbNotificacion(payload: IRNAcuerdoNotificacion): Promise<any> {
    return firstValueFrom(
      this.http.post(
        `${environment.acuerdos.notificacion}/mrb/nuevoAcuerdo`,
        payload
      )
    );
  }

  nuevoTemaMrbNotificacion(payload: IRNAcuerdoNotiNuevoTema): Promise<any> {
    return firstValueFrom(
      this.http.post(
        `${environment.acuerdos.notificacion}/mrb/nuevoTema`,
        payload
      )
    );
  }
}
